import React from 'react';

import { FormattedMessage, InjectedIntl } from 'react-intl';

import FeedbackCollector from '@atlaskit/feedback-collector';
import AutoDismissFlag, { FlagGroup } from '@atlaskit/flag';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import messages from './theme-selector.i18n';
import { ThemeFeedbackSuccessFlagProps } from './theme-selector.types';
import { urls } from './utils';

interface ThemeFeedbackCollectorInterface {
  userDisplayName?: string;
  userAtlassianAccountId?: string;
  userEmail?: string;
  intl: InjectedIntl;
  isFeedbackModalOpen: boolean;
  setFeedbackModalOpen: (value: boolean) => void;
  onFeedbackSubmit?: () => void;
}

export const ThemeFeedbackCollectorContext =
  React.createContext<ThemeFeedbackCollectorInterface>({
    intl: {} as InjectedIntl,
    isFeedbackModalOpen: false,
    setFeedbackModalOpen: () => {},
  });

export const ThemeSelectorFeedbackSuccessFlag = ({
  onClose,
  intl,
}: ThemeFeedbackSuccessFlagProps) => {
  return (
    <FlagGroup onDismissed={onClose}>
      <AutoDismissFlag
        id="theme-selector-feedback-success"
        testId="theme-selector-feedback-success-flag"
        title={intl.formatMessage(messages.feedbackSubmitFlagTitle)}
        description={intl.formatMessage(messages.feedbackSubmitFlagText)}
        icon={
          <SuccessIcon
            primaryColor={token('color.icon.success', colors.G300)}
            label="success"
          />
        }
      />
    </FlagGroup>
  );
};

export const ThemeSelectorFeedbackCollector = (): JSX.Element => {
  const {
    userEmail,
    userDisplayName,
    userAtlassianAccountId,
    intl,
    isFeedbackModalOpen,
    setFeedbackModalOpen,
    onFeedbackSubmit,
  } = React.useContext(ThemeFeedbackCollectorContext);

  // Entrypoint will be the same as nav feedback collector
  const entrypointId = '3e0d44da-9d2b-43b6-9119-e17b876ee1b4';

  return (
    <>
      {isFeedbackModalOpen && (
        <div id="theme-selector-feedback-modal">
          <FeedbackCollector
            locale={intl.locale}
            entrypointId={entrypointId}
            atlassianAccountId={userAtlassianAccountId}
            name={userDisplayName}
            email={userEmail}
            onClose={() => setFeedbackModalOpen(false)}
            onSubmit={onFeedbackSubmit}
            additionalFields={[
              {
                // "Feedback Context" custom field
                id: 'customfield_10047',
                value: `URL: ${window.location.href}\nUser Agent: ${window.navigator.userAgent}\nlabel: dark-theme`,
              },
            ]}
            showTypeField={false}
            feedbackTitle={intl.formatMessage(messages.giveFeedbackTitle)}
            feedbackTitleDetails={
              <>
                <FormattedMessage
                  {...messages.giveFeedbackTitleDetailsOne}
                  tagName="p"
                />
                <FormattedMessage
                  {...messages.giveFeedbackTitleDetailsTwo}
                  values={{
                    supportSiteLink: (
                      <a href={urls.contactSupportLink}>
                        <FormattedMessage {...messages.supportSiteLinkText} />
                      </a>
                    ),
                  }}
                  tagName="p"
                />
              </>
            }
            canBeContactedLabel={
              <FormattedMessage
                {...messages.canContactLabel}
                values={{
                  privacyPolicyLink: (
                    <a href={urls.privacyPolicy}>
                      <FormattedMessage {...messages.privacyPolicyLinkText} />
                    </a>
                  ),
                }}
                tagName="p"
              />
            }
            enrolInResearchLabel={
              <FormattedMessage
                {...messages.enrollLabel}
                values={{
                  atlassianResearchGroupLink: (
                    <a href={urls.atlassianResearchGroup}>
                      <FormattedMessage
                        {...messages.atlassianResearchGroupLinkText}
                      />
                    </a>
                  ),
                }}
                tagName="p"
              />
            }
            summaryPlaceholder={intl.formatMessage(
              messages.giveFeedbackPlaceholder
            )}
          />
        </div>
      )}
    </>
  );
};
